import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';

const Home = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="home-page">
      <SEO 
        title={null}
        description="Love Letters transforma cartas de amor em objectos que podes vestir. Merchandising poetico independente."
        url="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-start pt-20 md:pt-24">
        <div className="absolute inset-0">
          <img
            src="/img/hero-bg.png"
            alt="Love letter background"
            className="w-full h-full object-cover opacity-20"
          />
        </div>

        <div className="relative w-full px-4 md:px-8 lg:px-12 py-8 md:py-12 z-10">
          <div className="max-w-7xl mx-auto">
            {/* Hero text - slightly reduced, positioned higher */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="font-syne font-extrabold text-[2.5rem] sm:text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.9] tracking-tighter mb-6 md:mb-10"
            >
              WE <span className="text-accent">LOVE</span><br />
              LOVE<br />
              LETTERS<span className="text-accent">.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="font-serif text-lg md:text-xl italic text-foreground/80 mb-6 md:mb-8 max-w-xl"
            >
              {t('Sim, adoramos cartas de amor.', 'Yes, we love love letters.')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-8 md:px-10 py-4 bg-foreground text-background hover:bg-accent hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 font-courier font-bold uppercase text-xs md:text-sm tracking-wider"
                data-testid="hero-cta"
              >
                {t('Entrar na loja', 'Enter the shop')}
                <span>→</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WRITE THAT LOVE LETTER Section - Improved spacing */}
      <section className="py-20 md:py-32 px-4 md:px-8 lg:px-16 bg-background">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div className="p-6 md:p-16 flex items-center justify-center aspect-square">
            <h2 className="font-syne font-extrabold text-4xl sm:text-5xl md:text-7xl lg:text-8xl uppercase leading-none tracking-tighter text-accent text-center">
              WRITE<br />
              THAT LOVE<br />
              LETTER.
            </h2>
          </div>
          
          <div className="space-y-8 font-serif text-lg md:text-xl leading-relaxed text-foreground/80">
            <p>
              {t(
                'A Love Letters nasceu pelo desejo de recuperar e renovar a magia de escrever — e receber — cartas de amor.',
                'Love Letters was born from the desire to recover and renew the magic of writing — and receiving — love letters.'
              )}
            </p>
            <p>
              {t(
                'E, sim, as nossas cartas têm uma forma diferente. São para usar junto ao peito, coladas à pele, são para beijar o ombro, para vestir paredes.',
                'And yes, our letters have a different form. They are meant to be worn close to the heart, against the skin, to kiss the shoulder, to dress walls.'
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Text Section - More breathing room */}
      <section className="py-20 md:py-32 px-4 md:px-8 lg:px-16 bg-secondary/20 border-t border-border">
        <div className="max-w-[900px] mx-auto text-center">
          <p className="font-serif text-xl md:text-2xl lg:text-3xl leading-relaxed text-foreground/70 mb-12 md:mb-20">
            {t(
              'E quando as palavras não parecem ser suficientes, ou em silêncio se diz tudo o que é verdadeiramente necessário, para cada uma destas nossas cartas — que esperamos que façam vossas —, escolhemos uma imagem que representa o ambiente que a inspirou.',
              'And when words don\'t seem to be enough, or in silence everything that is truly necessary is said, for each of these letters — which we hope will become yours — we choose an image that represents the environment that inspired it.'
            )}
          </p>

          <h2 className="font-syne text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-tight">
            THIS IS A <span className="text-accent">LOVE<br/>POEM.</span>
          </h2>
        </div>
      </section>

      {/* Banner - Mobile: faster animation, less space between text */}
      <div className="mt-10 md:mt-16 mb-10 md:mb-16">
        <Link to="/shop" className="block">
          <div className="bg-accent overflow-hidden py-3 md:py-4">
            <div className="banner-scroll flex items-center">
              <span className="inline-block whitespace-nowrap font-courier font-black text-base md:text-2xl tracking-widest text-white uppercase">
                WE LOVE LOVE LETTERS&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;VER COLECÇÃO&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;WE LOVE LOVE LETTERS&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;VER COLECÇÃO&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </span>
              <span className="inline-block whitespace-nowrap font-courier font-black text-base md:text-2xl tracking-widest text-white uppercase">
                WE LOVE LOVE LETTERS&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;VER COLECÇÃO&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;WE LOVE LOVE LETTERS&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;VER COLECÇÃO&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </span>
            </div>
          </div>
        </Link>
      </div>

      <style>{`
        @keyframes banner-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .banner-scroll {
          animation: banner-scroll 30s linear infinite;
        }
        @media (max-width: 768px) {
          .banner-scroll {
            animation: banner-scroll 8s linear infinite;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;
